/**
 * Shared helpers for form component generation
 */

import fs from 'node:fs'
import path from 'node:path'
import { formFieldToZodType } from '@core-engine/schema/type-mappers/index.js'
import type { FormField, GeneratedFile } from '@core-engine/schema/types.js'
import { singularize } from '@core-engine/utils/string.js'

export interface StepResult {
  files: GeneratedFile[]
}

const ADMIN_CUSTOM_COMPONENTS = new Set(['media-field'])

/**
 * Check if a component exists in the user's project.
 * Shadcn registry components resolve from components/ui; BetterStart custom components resolve from components/custom.
 */
export function resolveUiImport(cwd: string, componentName: string): string {
  const componentDirectory = ADMIN_CUSTOM_COMPONENTS.has(componentName)
    ? 'components/custom'
    : 'components/ui'
  const locations = [componentDirectory, `src/${componentDirectory}`]
  for (const loc of locations) {
    if (
      fs.existsSync(path.join(cwd, loc, `${componentName}.tsx`)) ||
      fs.existsSync(path.join(cwd, loc, `${componentName}.ts`))
    ) {
      return `@/${componentDirectory}/${componentName}`
    }
  }
  return `@admin/${componentDirectory}/${componentName}`
}

/** Escape text for safe use in JSX content */
export function escapeJsx(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/** Emit a FormLabel line: visible when label is truthy, sr-only fallback to `name` when empty. */
export function formLabelLine(
  name: string,
  label: string,
  requiredStar: string,
  className?: string
): string {
  if (!label) return `<FormLabel className="sr-only">${name}${requiredStar}</FormLabel>`
  return className
    ? `<FormLabel className="${className}">${label}${requiredStar}</FormLabel>`
    : `<FormLabel>${label}${requiredStar}</FormLabel>`
}

function labelWithDescriptionLine(
  labelJSX: string,
  descriptionJSX: string,
  indent: string
): string {
  if (!descriptionJSX) return labelJSX
  return `<div className="flex flex-col gap-1">
${indent}  ${labelJSX}
${indent}  ${descriptionJSX}
${indent}</div>`
}

function formLabelWithDescriptionLine(
  name: string,
  label: string,
  requiredStar: string,
  descriptionJSX: string,
  indent: string,
  className?: string
): string {
  return labelWithDescriptionLine(
    formLabelLine(name, label, requiredStar, className),
    descriptionJSX,
    indent
  )
}

function formItemProps(height: FormField['height'], className?: string): string {
  if (height === 'fill' && className) return ` className="flex-1 min-h-0 ${className}"`
  if (height === 'fill') return ' className="flex-1 min-h-0"'
  return className ? ` className="${className}"` : ''
}

function fillControlProps(height: FormField['height'], className?: string): string {
  if (className && height === 'fill') return ` className="${className} flex-1"`
  if (className) return ` className="${className}"`
  return height === 'fill' ? ' className="flex-1"' : ''
}

function fillFormItemProps(height: FormField['height']): string {
  return formItemProps(height, height === 'fill' ? 'flex flex-col' : undefined)
}

/** Flatten form fields, resolving groups and skipping dynamic fields */
export function flattenFormFields(fields: FormField[]): FormField[] {
  return fields.flatMap((field) => {
    if (field.type === 'dynamicFields') return []
    if (field.type === 'group' && field.fields) return flattenFormFields(field.fields)
    return field.type === 'group' ? [] : [field]
  })
}

/** Build Zod schema field entries from form fields */
export function buildZodFields(fields: FormField[]): string {
  return fields
    .filter((f) => f.name)
    .map((f) => `  ${f.name}: ${formFieldToZodType(f)}`)
    .join(',\n')
}

/** Build default value entries from form fields */
export function buildDefaultValues(fields: FormField[]): string {
  return fields
    .filter((f) => f.name)
    .map((f) => {
      if (f.type === 'checkbox') return `    ${f.name}: false`
      if (f.type === 'number') return `    ${f.name}: undefined`
      if (f.type === 'multiselect' || f.type === 'list') return `    ${f.name}: []`
      if (f.type === 'radio' && f.defaultValue !== undefined)
        return `    ${f.name}: '${f.defaultValue}'`
      if (f.type === 'select' || f.type === 'radio') return `    ${f.name}: undefined`
      if (f.defaultValue !== undefined) return `    ${f.name}: '${f.defaultValue}'`
      return `    ${f.name}: ''`
    })
    .join(',\n')
}

/** Build useFieldArray declarations for list fields */
export function buildFieldArrayDecls(listFields: FormField[]): string {
  return listFields
    .map(
      (f) =>
        `  const ${f.name}FieldArray = useFieldArray({ control: form.control, name: '${f.name}' })`
    )
    .join('\n')
}

/** Build watch variable declarations for showWhen fields */
export function buildWatchDecls(fields: FormField[]): { setup: string; hasWatch: boolean } {
  const watchFields = new Set<string>()
  for (const f of fields) {
    if (f.showWhen) {
      watchFields.add(f.showWhen.field)
    }
  }
  if (watchFields.size === 0) return { setup: '', hasWatch: false }
  const decls = Array.from(watchFields)
    .map((wf) => `  const ${wf}Value = form.watch('${wf}')`)
    .join('\n')
  return { setup: `\n${decls}\n`, hasWatch: true }
}

/** Get list fields that need useFieldArray */
export function getListFields(fields: FormField[]): FormField[] {
  return fields.filter((f) => f.name && f.type === 'list' && f.fields && f.fields.length > 0)
}

export function hasFormDescriptionFields(fields: FormField[]): boolean {
  return fields.some((field) => {
    if (field.hint) return true
    if ((field.type === 'group' || field.type === 'list') && field.fields) {
      return hasFormDescriptionFields(field.fields)
    }
    return false
  })
}

export function hasSelectFields(fields: FormField[]): boolean {
  return fields.some((field) => {
    if (field.type === 'select' && field.options && field.options.length > 0) {
      return true
    }
    if ((field.type === 'group' || field.type === 'list') && field.fields) {
      return hasSelectFields(field.fields)
    }
    return false
  })
}

/**
 * Render an array of form fields to JSX, preserving group layout.
 * Group fields render as a responsive grid wrapper; other fields render normally.
 */
export function renderFieldsJSX(fields: FormField[]): string {
  return fields
    .filter((f) => !f.hidden)
    .map((f) => {
      if (f.type === 'dynamicFields') return ''
      if (f.type === 'group' && f.fields) {
        const cols = f.columns || 2
        const innerJSX = renderFieldsJSX(f.fields)
        const groupJSX = `        <div className="grid grid-cols-1 md:grid-cols-${cols} gap-5">\n${innerJSX}\n        </div>`
        return f.showWhen ? wrapShowWhen(f, groupJSX) : groupJSX
      }
      if (!f.name) return ''
      return wrapShowWhen(f, generateFieldJSX(f))
    })
    .filter(Boolean)
    .join('\n\n')
}

/** Wrap field JSX with showWhen conditional if applicable */
export function wrapShowWhen(field: FormField, jsx: string): string {
  if (!field.showWhen) return jsx
  const watchVar = `${field.showWhen.field}Value`
  const { value } = field.showWhen
  if (Array.isArray(value)) {
    const vals = value.map((v) => `'${v}'`).join(', ')
    return `        {[${vals}].includes(${watchVar} as string) && (\n${jsx}\n        )}`
  }
  return `        {${watchVar} === '${value}' && (\n${jsx}\n        )}`
}

/** Generate JSX for a single form field */
export function generateFieldJSX(field: FormField): string {
  const name = field.name || ''
  const label = field.label ? escapeJsx(field.label) : ''
  const placeholder = field.placeholder || ''
  const hint = field.hint || ''

  const hintJSX = hint ? `<FormDescription>${escapeJsx(hint)}</FormDescription>` : ''
  const hintPlainJSX = hint
    ? `<p className="text-sm text-muted-foreground">${escapeJsx(hint)}</p>`
    : ''
  const requiredStar = field.required ? ' <span className="text-destructive">*</span>' : ''

  switch (field.type) {
    case 'textarea':
      return `        <FormField
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <FormItem${fillFormItemProps(field.height)}>
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <FormControl>
                <Textarea${fillControlProps(field.height)} placeholder="${placeholder}" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />`

    case 'radio':
      if (field.options && field.options.length > 0) {
        const radioItems = field.options
          .map(
            (opt) =>
              `                    <FormLabel htmlFor="${name}-${opt.value}" className="flex items-center space-x-2 text-sm font-medium leading-none">
                      <RadioGroupItem value="${opt.value}" id="${name}-${opt.value}" />
                      <span>${escapeJsx(opt.label)}</span>
                    </FormLabel>`
          )
          .join('\n')
        return `        <FormField
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <FormItem${formItemProps(field.height, 'space-y-3')}>
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <FormControl>
                <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="flex items-center space-x-2">
${radioItems}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />`
      }
      return generateTextFieldJSX(
        name,
        label,
        placeholder,
        hintJSX,
        requiredStar,
        'text',
        field.height
      )

    case 'select':
      if (field.options && field.options.length > 0) {
        const optionItems = field.options
          .map(
            (opt) =>
              `                    <SelectItem value="${opt.value}">${escapeJsx(opt.label)}</SelectItem>`
          )
          .join('\n')
        return `        <FormField
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <FormItem${formItemProps(field.height)}>
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="${placeholder || 'Select...'}" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
${optionItems}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />`
      }
      return generateTextFieldJSX(
        name,
        label,
        placeholder,
        hintJSX,
        requiredStar,
        'text',
        field.height
      )

    case 'checkbox':
      return `        <FormField
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <FormItem${formItemProps(field.height, 'flex flex-row items-start space-x-3 space-y-0')}>
              <FormControl>
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={field.onChange}
                  className="mt-1"
                />
              </FormControl>
              <div className="space-y-2 leading-none">
                ${formLabelWithDescriptionLine(name, label || name, requiredStar, hintJSX, '                ')}
                <FormMessage />
              </div>
            </FormItem>
          )}
        />`

    case 'email':
      return generateTextFieldJSX(
        name,
        label,
        placeholder || 'email@example.com',
        hintJSX,
        requiredStar,
        'email',
        field.height
      )

    case 'number':
      return generateNumberFieldJSX(name, label, placeholder, hintJSX, requiredStar, field.height)

    case 'date':
      return generateTextFieldJSX(
        name,
        label,
        placeholder,
        hintJSX,
        requiredStar,
        'date',
        field.height
      )

    case 'url':
      return generateTextFieldJSX(
        name,
        label,
        placeholder || 'https://',
        hintJSX,
        requiredStar,
        'url',
        field.height
      )

    case 'phone':
      return generateTextFieldJSX(
        name,
        label,
        placeholder || '+1 (555) 000-0000',
        hintJSX,
        requiredStar,
        'tel',
        field.height
      )

    case 'file':
    case 'upload':
      return generateFileUploadFieldJSX(field, name, label, hintJSX, requiredStar)

    case 'list':
      return generateListFieldJSX(field, name, label, hintPlainJSX, requiredStar)

    default:
      return generateTextFieldJSX(
        name,
        label,
        placeholder,
        hintJSX,
        requiredStar,
        'text',
        field.height
      )
  }
}

export function generateTextFieldJSX(
  name: string,
  label: string,
  placeholder: string,
  hintJSX: string,
  requiredStar: string,
  inputType: string,
  height?: FormField['height']
): string {
  return `        <FormField
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <FormItem${formItemProps(height)}>
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <FormControl>
                <Input type="${inputType}" placeholder="${placeholder}" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />`
}

function generateNumberFieldJSX(
  name: string,
  label: string,
  placeholder: string,
  hintJSX: string,
  requiredStar: string,
  height?: FormField['height']
): string {
  return `        <FormField
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <FormItem${formItemProps(height)}>
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <FormControl>
                <Input
                  type="number"
                  placeholder="${placeholder}"
                  value={field.value ?? ''}
                  onChange={(event) => {
                    const nextValue = event.target.value === '' ? undefined : event.target.valueAsNumber
                    const normalizedValue = Number.isNaN(nextValue) ? undefined : nextValue
                    field.onChange(normalizedValue)
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />`
}

function generateFileUploadFieldJSX(
  field: FormField,
  name: string,
  label: string,
  hintJSX: string,
  requiredStar: string
): string {
  const accept = field.accept || '*/*'
  return `        <FormField
          control={form.control}
          name="${name}"
          render={({ field }) => (
            <FormItem${formItemProps(field.height)}>
              ${formLabelWithDescriptionLine(name, label, requiredStar, hintJSX, '              ')}
              <FormControl>
                <MediaField
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  accept="${accept}"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />`
}

function generateListFieldJSX(
  field: FormField,
  name: string,
  label: string,
  hintJSX: string,
  requiredStar: string
): string {
  if (!field.fields || field.fields.length === 0) {
    return generateTextFieldJSX(
      name,
      label,
      field.placeholder || '',
      hintJSX,
      requiredStar,
      'text',
      field.height
    )
  }

  const singularLabel = singularize(label || name)

  const nestedFieldsJSX = field.fields
    .map((nf) => {
      const nfName = nf.name || ''
      const nfLabelRaw = nf.label ? escapeJsx(nf.label) : ''
      const nfPlaceholder = nf.placeholder || ''

      if (nf.type === 'select' && nf.options && nf.options.length > 0) {
        const selectItems = nf.options
          .map(
            (opt) =>
              `                          <SelectItem value="${opt.value}">${escapeJsx(opt.label)}</SelectItem>`
          )
          .join('\n')
        return `                    <FormField
                      control={form.control}
                      name={\`${name}.\${index}.${nf.name}\`}
                      render={({ field: formField }) => (
                        <FormItem className="flex-1">
                          ${formLabelLine(nfName, nfLabelRaw, '')}
                          <Select onValueChange={formField.onChange} defaultValue={formField.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="${nf.placeholder || 'Select...'}" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
${selectItems}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />`
      }
      if (nf.type === 'number') {
        return `                    <FormField
                      control={form.control}
                      name={\`${name}.\${index}.${nf.name}\`}
                      render={({ field: formField }) => (
                        <FormItem className="flex-1">
                          ${formLabelLine(nfName, nfLabelRaw, '')}
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="${nfPlaceholder}"
                              value={formField.value ?? ''}
                              onChange={(event) => {
                                const nextValue = event.target.value === '' ? undefined : event.target.valueAsNumber
                                const normalizedValue = Number.isNaN(nextValue) ? undefined : nextValue
                                formField.onChange(normalizedValue)
                              }}
                              onBlur={formField.onBlur}
                              name={formField.name}
                              ref={formField.ref}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />`
      }

      return `                    <FormField
                      control={form.control}
                      name={\`${name}.\${index}.${nf.name}\`}
                      render={({ field: formField }) => (
                        <FormItem className="flex-1">
                          ${formLabelLine(nfName, nfLabelRaw, '')}
                          <FormControl>
                            <Input type="text" placeholder="${nfPlaceholder}" {...formField} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />`
    })
    .join('\n')

  const defaultObj = field.fields
    .map((nf) => {
      if (nf.type === 'select' || nf.type === 'radio') return `${nf.name}: undefined`
      if (nf.type === 'checkbox') return `${nf.name}: false`
      if (nf.type === 'number') return `${nf.name}: undefined`
      return `${nf.name}: ''`
    })
    .join(', ')

  return `        <div className="space-y-2">
          <FormField
            control={form.control}
            name="${name}"
            render={() => (
              <FormItem className="space-y-0">
                ${labelWithDescriptionLine(formLabelLine(name, label, requiredStar, 'text-sm font-medium leading-none'), hintJSX, '                ')}
              </FormItem>
            )}
          />
          {${name}FieldArray.fields.map((item, index) => (
            <div key={item.id} className="relative rounded-lg border p-4">
              <button
                type="button"
                onClick={() => ${name}FieldArray.remove(index)}
                className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="size-4" />
                <span className="sr-only">Remove</span>
              </button>
              <div className="space-y-4 pr-8">
${nestedFieldsJSX}
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={() => ${name}FieldArray.append({ ${defaultObj} })}
          >
            Add ${singularLabel}
          </Button>
        </div>`
}
